import React, { useCallback } from 'react';
import styled from 'styled-components';

import { toRem } from '@front/common/src/utils/styles';
import { SocialButtonProps } from '@front/common/src/components/social-button/types';
import { useOnClickThrottled } from '@front/common/src/hooks/use-on-click-throttled';

import { translate } from '@front/i18n';

import Icon from '../icon/icon';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import { WithClassNameProps } from '../types';

import { getColors, StyledButton, StyledLoadingWrapper, BUTTON_TEXT_BY_SIZE } from './common';

const StyledIcon = styled(Icon)`
  position: absolute;
  left: ${toRem(15)};
  top: 0;
  bottom: 0;
  margin: auto;
`;

const t = translate('oauth');

const SocialButton = ({
  onPress,
  provider,
  isDisabled = false,
  isLoading = false,
  authType = 'signin',
  className,
}: SocialButtonProps & WithClassNameProps) => {
  const ButtonText = BUTTON_TEXT_BY_SIZE.lg;

  const { color, backgroundColor, borderColor, loadingSpinnerColor } = getColors('secondary', isLoading);

  // To avoid rapid double clicks
  const onPressThrottled = useOnClickThrottled(onPress);

  const shouldBeDisabled = isDisabled || isLoading;

  const onClickBtn = useCallback(() => {
    if (!shouldBeDisabled) {
      onPressThrottled();
    }
  }, [shouldBeDisabled, onPressThrottled]);

  return (
    <StyledButton
      className={className}
      type='button'
      disabled={shouldBeDisabled}
      $isDisabled={shouldBeDisabled}
      onClick={onClickBtn}
      $isRound
      $backgroundColor={backgroundColor}
      $borderColor={borderColor}
      $type='secondary'
      $size='lg'
      $hasFullWidth
    >
      <StyledIcon name={provider} size={24} title={provider} />
      {isLoading && (
        <StyledLoadingWrapper>
          <LoadingSpinner color={loadingSpinnerColor} title={t('components.button.loading')} />
        </StyledLoadingWrapper>
      )}
      <ButtonText color={color} centered>
        {t(`.${authType}`, { provider: t(`.providers.${provider}`) })}
      </ButtonText>
    </StyledButton>
  );
};

export default SocialButton;
